// U2 · a story chapter (QU2_Story). Text from shared chapters.ts (paras + the buddy's quote); chapter 3 has the one
// choice: POST /api/story/3/choice, then "You: {choice}" + the after line with {choice} filled in. Opening the newest
// chapter clears its NEW tag (POST /api/story/:chap/read).
import { useEffect, useState } from 'preact/hooks';
import { C } from '@shared/content/characters';
import { CHAPTER, CHAPTER_LINE, STAGE_CAP } from '@shared/content/story';
import { chapterText } from '@shared/content/chapters';
import { COMING_SOON_LABEL } from '@shared/rules';
import { useApp, storyChoice, storyRead } from '../../store';
import { back, replace, useParams } from '../../router';
import { Screen, ScrollArea, IconButton, TextButton, Icon } from '../../components';
import { US } from './copy';
import s from './us.module.css';

export function StoryScreen() {
  const st = useApp((x) => x.state);
  const params = useParams();
  const [busy, setBusy] = useState(false);
  const k = Math.max(1, Math.min(7, Math.round(Number(params.chap) || 3)));
  const ch = st?.ch || 'margo';
  const b = st?.buddy;
  const open = !!b && k <= b.chaptersOpen;
  const newest = b?.newChapter === k;
  useEffect(() => { if (open && newest) void storyRead(k); }, [k, open, newest]);
  if (!st || !b) return <Screen theme={ch} id="U2" />;

  const c = C[ch];
  const text = chapterText(ch, k);
  const title = CHAPTER[ch][k - 1];
  const paras = text ? text.paras : [CHAPTER_LINE[ch][k - 1], STAGE_CAP[ch][k - 1]];
  const choice = text?.choice || null;
  const picked = b.chapterChoices[String(k)] || null;
  const stopOf = (n: number) => b.route.find((r) => r.type === 'chap' && r.chap === n) || null;
  const lock = stopOf(k);

  const pick = async (o: string) => {
    if (busy) return;
    setBusy(true);
    await storyChoice(k, o);
    setBusy(false);
  };

  // What comes after this chapter.
  let foot = null;
  if (k < 7) {
    const n = k + 1, stop = stopOf(n);
    if (n <= b.chaptersOpen) {
      foot = (
        <TextButton class={s.footLink} tone="accent" weight={700} size={14} onClick={() => replace('U2', { chap: n })}>
          {`Chapter ${n}: ${CHAPTER[ch][n - 1]}`}
          <Icon name="chevron" size={16} />
        </TextButton>
      );
    } else {
      foot = <p class={s.foot}>{stop && stop.ready ? `Chapter ${n} opens at level ${stop.level}.` : `Chapter ${n}: ${COMING_SOON_LABEL}.`}</p>;
    }
  } else foot = <p class={s.foot}>{US.storyEnd}</p>;

  return (
    <Screen theme={ch} id="U2">
      <ScrollArea class={s.storyScroll} fadeTop={0}>
        <div class={s.hero}>
          <img src={c.assets.chap(k)} alt={`Chapter ${k}: ${title}`} style={{ objectPosition: k === 1 ? '50% 20%' : '50% 45%', opacity: open ? 1 : 0.4 }} />
          <div class={s.heroFade} aria-hidden="true" />
        </div>
        <article class={s.body}>
          <p class={s.kicker}>CHAPTER {k}</p>
          <h1 class={s.title}>{title}</h1>
          {!open ? (
            <p class={s.para}>{lock && lock.ready ? `Opens at level ${lock.level}.` : `${COMING_SOON_LABEL}.`}</p>
          ) : (
            <>
              {paras.map((p, i) => <p class={s.para} key={i}>{p}</p>)}
              {text?.quote && (
                <blockquote class={s.quote}>
                  {`“${text.quote}”`}
                  <span class={s.quoteBy}>{`— ${c.name}`}</span>
                </blockquote>
              )}
              {choice && !picked && (
                <>
                  <p class={s.prompt}>{choice.prompt}</p>
                  {choice.options.map((o) => (
                    <button type="button" class={s.option} key={o} aria-disabled={busy ? 'true' : undefined} onClick={() => void pick(o)}>{o}</button>
                  ))}
                </>
              )}
              {choice && picked && (
                <>
                  <p class={s.chosen}>{`You: ${picked}`}</p>
                  <p class={`${s.para} ${s.after}`}>{choice.after.replace('{choice}', picked)}</p>
                </>
              )}
            </>
          )}
          {open && foot}
        </article>
      </ScrollArea>
      <IconButton icon="back" label="Back" class={s.floatBack} onClick={() => back('U1')} />
    </Screen>
  );
}
